/** @jsxImportSource @emotion/react */

import React, { KeyboardEvent, useEffect, useRef } from 'react';
import { useForm } from '../../context/FormContext/FormContext';
import { Hint } from '../Hint/Hint';
import { useTheme } from '../../ThemeProvider/ThemeProvider';
import { CSSProp } from '../../../types/emotion';

export interface FormTextareaProps {
  /**
   * The id of the textarea, needs to be the name of the property of the form
   */
  id: string;
  /**
   * The label of the textarea
   */
  label?: string;
  /**
   * The placeholder of the textarea
   */
  placeholder?: string;
  /**
   * Whether the input value is required
   */
  required?: boolean;
  /**
   * Messages to be displayed when the input validation shows an error
   */
  messages?: { [key: string]: string };
  /**
   * Whether the textarea is disabled
   */
  disabled?: boolean;
  /**
   * The height of the textarea in rem
   */
  height?: number;
  /**
   * Whether the textarea should resize automatically, false by default
   */
  autoResize?: boolean;
  /**
   * The maximum number of rows the textarea should have in px
   */
  maxHeight?: number;
  /**
   * Whether the form should be submitted when the user presses enter inside the field
   */
  submitOnEnter?: boolean;
}

/**
 * A textarea component to be used inside a `FormContext`
 */
export function FormTextarea({
  id,
  label,
  placeholder,
  required,
  messages = {},
  disabled,
  height,
  autoResize = false,
  maxHeight,
  submitOnEnter = false,
  ...props
}: FormTextareaProps & Omit<Partial<React.HTMLAttributes<HTMLDivElement>>, keyof FormTextareaProps> & CSSProp) {
  const { tokens } = useTheme();
  const { inputHandlers, touched, errors } = useForm();
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  // React uses focusout events for the onBlur event, which are not triggered by autofill, so we have to listen for real onblur events manually
  const inputRef = useRef<any>();
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.addEventListener('blur', handleOnBlur);
    }
  }, [inputRef]);

  const handleOnBlur = () => {
    inputHandlers(id).onBlur();
  };

  const handleOnKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (submitOnEnter && e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (Object.values(errors).every(e => e === '')) {
        inputHandlers(id).onSubmit();
      }
    }
  };

  // Fix auto height resizing
  useEffect(() => {
    if (autoResize && textAreaRef.current) {
      textAreaRef.current.style.height = 'auto';
      textAreaRef.current.style.height = `${
        maxHeight ? Math.min(maxHeight, textAreaRef.current.scrollHeight) : textAreaRef.current.scrollHeight
      }px`;
    }
  }, [autoResize, maxHeight, textAreaRef.current?.value]);

  return (
    <div
      {...props}
      css={{
        background: tokens.colors['bosch-gray-90'],
        fontFamily: tokens.styles['textArea-content-label-initial-fontFamily'],
        fontSize: tokens.styles['textArea-content-label-initial-fontSize'],
        fontWeight: tokens.styles['textArea-content-label-initial-fontWeight'],
        padding: '0.75rem',
        width: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        height: autoResize || height === undefined ? undefined : `${height ? height : 0}rem`,
      }}
    >
      {label && (
        <label htmlFor={id}>
          {label}
          {required && ' *'}
        </label>
      )}
      <textarea
        id={id}
        ref={textAreaRef}
        {...inputHandlers(id)}
        onKeyDown={handleOnKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        css={{
          flex: autoResize ? undefined : 1,
          border: 'none',
          resize: 'none',
          background: 'inherit',
          fontFamily: 'inherit',
          fontSize: '1rem',
          fontWeight: 'inherit',
          width: '100%',
          outline: 'none',
        }}
        aria-describedby={`${id}-error`}
      />
      {touched[id] && errors[id] && (
        <Hint id={`${id}-error`} level="error" description={messages?.[errors[id]]} aria-live="polite" />
      )}
    </div>
  );
}
